import { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { Edit } from "components/icons/generated";
import KeyValue from "components/KeyValue";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Tag from "ds/components/Tag";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { ScanFormFields } from "shared/Scan/types";

import { SCHEDULE_SCAN_WIZARD_STEP_HEADERS } from "../constants";
import { NewScanScheduleContext } from "../context";
import { NewScanScheduleStep } from "../types";

// Right-aligned chips, mirroring how the stack-creation summary renders list values.
const TagList = ({ values }: { values: string[] }) => (
  <Box gap="medium" justify="end" wrap>
    {values.map((value) => (
      <Tag key={value} tag={value} />
    ))}
  </Box>
);

// A summary section wrapped in a collapsible card (mirrors NewStack's summary Panel).
const SummaryPanel = ({
  title,
  step,
  count,
  children,
}: {
  title: string;
  step: NewScanScheduleStep;
  count?: number;
  children: ReactNode;
}) => {
  const { goToStep } = useTypedContext(NewScanScheduleContext);
  const [isCollapsed, toggle] = useToggle(false);

  return (
    <CardCollapsible
      fullWidth
      ariaLevel={3}
      ariaLabel={title}
      title={title}
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={count !== undefined ? <Counter count={count} /> : undefined}
      headerSuffix={
        <ButtonIcon icon={Edit} variant="ghost" onPress={() => goToStep(step)}>
          Edit step
        </ButtonIcon>
      }
    >
      {children}
    </CardCollapsible>
  );
};

const NewScanScheduleStepSummary = () => {
  const { watch } = useFormContext<ScanFormFields>();

  const {
    formOptions: { integrations, regions: regionOptions, workerPools },
  } = useTypedContext(NewScanScheduleContext);

  const formValues = watch();

  const integrationName = integrations.find(
    (integration) => integration.id === formValues.integrationId
  )?.name;

  const regionLabels = formValues.regions.map((region) => {
    const option = regionOptions.find((regionOption) => regionOption.id === region);
    return option ? `${option.location} (${option.id})` : region;
  });

  const workerPoolName = workerPools.find((pool) => pool.id === formValues.workerPoolId)?.name;

  return (
    <FullScreenModalBody>
      <FullScreenModalBodyHeader
        {...SCHEDULE_SCAN_WIZARD_STEP_HEADERS[NewScanScheduleStep.Summary]}
      />

      <Box direction="column" gap="large">
        <SummaryPanel title="Scan details" step={NewScanScheduleStep.ScanDetails}>
          <KeyValue name="Name">{formValues.name || "–"}</KeyValue>
          <KeyValue name="Space">{formValues.space || "–"}</KeyValue>
          <KeyValue name="Labels">
            {formValues.labels.length > 0 ? (
              <TagList values={formValues.labels.map((label) => label.value)} />
            ) : (
              "–"
            )}
          </KeyValue>
          <KeyValue name="Description">{formValues.description || "–"}</KeyValue>
        </SummaryPanel>
        <SummaryPanel title="Attach provider" step={NewScanScheduleStep.AttachProviders}>
          <KeyValue name="Provider">AWS</KeyValue>
          <KeyValue name="Provider version">{formValues.providerVersion || "–"}</KeyValue>
          <KeyValue name="Integration">{integrationName || "–"}</KeyValue>
          <KeyValue name="Regions">
            {regionLabels.length > 0 ? <TagList values={regionLabels} /> : "–"}
          </KeyValue>
        </SummaryPanel>
        <SummaryPanel title="Attach worker pool" step={NewScanScheduleStep.AttachWorkerPool}>
          <KeyValue name="Worker pool">{workerPoolName || "–"}</KeyValue>
        </SummaryPanel>

        <SummaryPanel
          title="Scope of resources"
          step={NewScanScheduleStep.DefineScope}
          count={formValues.resourceTypes.length}
        >
          <KeyValue name="Resource types">
            {formValues.resourceTypes.length > 0 ? (
              <TagList values={formValues.resourceTypes} />
            ) : (
              "–"
            )}
          </KeyValue>
        </SummaryPanel>
      </Box>
    </FullScreenModalBody>
  );
};

export default NewScanScheduleStepSummary;
